/**
 * Seated mode: play sitting down (Meta's "airplane seat" use) and still see
 * the world from standing eye height. The rig is lifted by `applied` metres;
 * everything that reasons about feet (collision, the first-person body, the
 * position other players receive) subtracts it, so your avatar stays on the
 * floor for you and for everyone else.
 *
 * While you sit in an in-world seat the seated-mode lift is dropped — you're
 * seated in real life too, so seat height is already right. The exception is
 * a game-table chair: there your eyes must clear the tabletop whatever your
 * real posture (a sofa, a slouch, a short real seat), so the rig gets a
 * `table` lift instead, measured from your actual headset height.
 */
const KEY = 'gathrxr.vrSeated';
const STANDING_EYE = 1.6;

function load(): { on: boolean; lift: number } {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (v && typeof v.on === 'boolean' && typeof v.lift === 'number') return v;
  } catch { /* storage unavailable */ }
  return { on: false, lift: 0 };
}

export const vrLift = { ...load(), applied: 0, table: 0 };

/** Lift wanted right now: the table lift in a seat (0 for ordinary seats), else seated mode's. */
export function wantedLift(inSeat: boolean) {
  if (inSeat) return vrLift.table;
  return vrLift.on ? vrLift.lift : 0;
}

/** Turn seated mode on (measuring how far below standing eye height you are) or off. */
export function setSeatedMode(on: boolean, headHeight: number) {
  vrLift.on = on;
  // Someone who turns it on while standing gets a small lift at most.
  vrLift.lift = on ? Math.min(.9, Math.max(.25, STANDING_EYE - headHeight)) : 0;
  try { localStorage.setItem(KEY, JSON.stringify({ on: vrLift.on, lift: vrLift.lift })); } catch { /* ignore */ }
}

// ---- Game tables ----

/** Eye height above the tabletop for a player seated at a game table (m). */
export const TABLE_EYE = 0.5;
/** Most the rig is raised at a table — a headset on the floor or a bad reading can't fling you up. */
export const TABLE_LIFT_MAX = 0.8;
/** A head-height change bigger than this (m)… */
const REMEASURE_DELTA = 0.12;
/** …held this long (ms) re-measures the lift (sat down / stood up after taking the seat). */
const REMEASURE_MS = 2000;

/**
 * Rig lift that puts the eyes TABLE_EYE above a tabletop. `topAboveRoot` is
 * the tabletop's height above the unlifted rig (the seat's root), `headY` the
 * headset's rig-local height. Only ever raises: someone standing at the table
 * already sees over it, and lowering them would put their real hands under it.
 */
export function tableLift(topAboveRoot: number, headY: number): number {
  if (!Number.isFinite(topAboveRoot) || !Number.isFinite(headY)) return 0;
  return Math.min(TABLE_LIFT_MAX, Math.max(0, topAboveRoot + TABLE_EYE - headY));
}

/**
 * Head-bob-proof table lift. The first reading after sitting sets it; after
 * that it only re-measures when the needed lift has stayed more than
 * REMEASURE_DELTA away for REMEASURE_MS (you sat down, stood up, or leaned
 * back into the sofa) — looking around or leaning in to read a card doesn't.
 */
export function createTableLiftTracker() {
  let lift: number | null = null;
  let since = 0;
  let sum = 0;
  let n = 0;
  return {
    reset() { lift = null; n = 0; },
    update(now: number, topAboveRoot: number, headY: number): number {
      const need = tableLift(topAboveRoot, headY);
      if (lift === null) { lift = need; n = 0; return lift; }
      if (Math.abs(need - lift) <= REMEASURE_DELTA) { n = 0; return lift; }
      if (n === 0) { since = now; sum = 0; }
      sum += need; n++;
      if (now - since >= REMEASURE_MS) { lift = sum / n; n = 0; }
      return lift;
    },
  };
}

/** Move `applied` toward `want` by at most `rate` m/s (no vertical snaps in the headset). */
export function easeLift(applied: number, want: number, dt: number, rate = 0.7): number {
  const step = rate * Math.min(Math.max(dt, 0), 0.1);
  return Math.abs(want - applied) <= step ? want : applied + Math.sign(want - applied) * step;
}
